import { inject, Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { CameraPreview, type CameraPreviewOptions } from '@capacitor-community/camera-preview';

import { Platform } from '@ionic/angular';

const CAMERA_HEIGHT_ANDROID = 220;
const CAMERA_Y_ANDROID = 200;
const CAPTURE_QUALITY = 85;
const LAYOUT_DELAY_MS = 100;

@Injectable()
export class PhotoService {
  private readonly platform = inject(Platform);

  async openCamera(parentElementId: string = 'cameraPreview'): Promise<void> {
    const isNative = Capacitor.isNativePlatform();

    // Wait for layout to complete on native
    if (isNative) {
      await new Promise((resolve) => setTimeout(resolve, LAYOUT_DELAY_MS));
    }

    let options: CameraPreviewOptions;

    if (isNative && this.platform.is('ios')) {
      const parentElement = document.getElementById(parentElementId);

      if (parentElement) {
        // Use the parent wrapper (flex container) for dimensions since the element itself may be empty
        const wrapper = parentElement.parentElement;
        const targetElement = wrapper || parentElement;
        const rect = targetElement.getBoundingClientRect();

        // Get iOS safe area top (status bar + notch/Dynamic Island)
        const safeAreaTop = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--ion-safe-area-top') || '0',
          10,
        );

        // Get screen width for full-width camera
        const screenWidth = window.screen.width;

        // Use full screen width, height from container
        const width = screenWidth;
        const height = Math.round(rect.height);

        // Camera plugin positions relative to full screen, but webview content
        // is offset by safe area. Add safe area + extra offset for correct position.
        const extraOffset = 100; // Additional offset to separate from top content
        const x = 0; // Edge to edge
        const y = Math.round(rect.top) + safeAreaTop + extraOffset;

        options = {
          position: 'rear',
          parent: parentElementId,
          disableAudio: true,
          x,
          y,
          width,
          height,
          toBack: false,
        };
      } else {
        options = {
          position: 'rear',
          parent: parentElementId,
          disableAudio: true,
          toBack: false,
        };
      }
    } else if (this.platform.is('android')) {
      options = {
        position: 'rear',
        parent: parentElementId,
        disableAudio: true,
        height: CAMERA_HEIGHT_ANDROID,
        y: CAMERA_Y_ANDROID,
        toBack: false,
      };
    } else {
      // Web
      options = {
        position: 'rear',
        parent: parentElementId,
        disableAudio: true,
        toBack: false,
      };
    }

    await CameraPreview.start(options);
  }

  async closeCamera(): Promise<void> {
    const video = document.getElementById('video') as HTMLVideoElement | null;
    if (video?.srcObject) {
      (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      video.remove();
    }
    await CameraPreview.stop();
  }

  async captureCamera(): Promise<string> {
    const result = await CameraPreview.capture({ quality: CAPTURE_QUALITY });
    return result.value;
  }
}
